import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Table, Button, Modal, Form, Select, InputNumber, Input, Space, message, Upload, Popconfirm } from "antd";
import { PlusOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import PageHeader from "../../components/ui/PageHeader";
import StatusTag from "../../components/ui/StatusTag";
import EmptyState from "../../components/ui/EmptyState";

const CATEGORY_OPTIONS = ["Travel", "Food", "Medical", "Office Supplies", "Other"].map((c) => ({ value: c, label: c }));

// Mirrors the backend's constants/currencies.ts — keep in sync if that
// list changes.
const CURRENCY_SYMBOLS = { PHP: "₱", USD: "$", INR: "₹", EUR: "€", GBP: "£", AUD: "A$", SGD: "S$", JPY: "¥", CNY: "¥", CAD: "C$" };
const CURRENCY_OPTIONS = Object.entries(CURRENCY_SYMBOLS).map(([code, symbol]) => ({ value: code, label: `${code} (${symbol})` }));
const currencySymbol = (code) => CURRENCY_SYMBOLS[code] || `${code} `;

const Reimbursement = () => {
  const { request } = useRequest();
  const [requests, setRequests] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [billFile, setBillFile] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [billSubmitting, setBillSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [billForm] = Form.useForm();
  const requestCurrency = Form.useWatch("currency", form) || "PHP";

  const fetchMine = () => {
    request({ url: apiPath.reimbursementsMine, method: "GET", onSuccess: (res) => setRequests(res?.data?.data || []) });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchMine, []);

  const openCreate = () => {
    form.resetFields();
    setReceiptFile(null);
    setSubmitting(false);
    setModalOpen(true);
  };

  const onSubmit = () => {
    // Without this guard, a slow request + no visible feedback on the OK
    // button led to users clicking it repeatedly, creating several
    // duplicate reimbursement requests for the same claim.
    if (submitting) return;
    form.validateFields().then((values) => {
      const formData = new FormData();
      formData.append("category", values.category);
      formData.append("description", values.description);
      formData.append("currency", values.currency || "PHP");
      formData.append("estimatedAmount", values.estimatedAmount);
      if (receiptFile) formData.append("file", receiptFile);

      setSubmitting(true);
      request({
        url: apiPath.reimbursements,
        method: "POST",
        data: formData,
        header: { "Content-Type": "multipart/form-data" },
        onSuccess: () => {
          setSubmitting(false);
          message.success("Reimbursement request submitted");
          setModalOpen(false);
          fetchMine();
        },
        onError: (err) => {
          setSubmitting(false);
          message.error(err?.response?.data?.message || "Could not submit reimbursement request");
        },
      });
    });
  };

  const onCancelRequest = (record) => {
    request({
      url: `${apiPath.reimbursements}/${record._id}/cancel`,
      method: "PUT",
      onSuccess: () => {
        message.success("Reimbursement request cancelled");
        fetchMine();
      },
      onError: (err) => message.error(err?.response?.data?.message || "Could not cancel reimbursement request"),
    });
  };

  const openBillModal = (record) => {
    setActiveRecord(record);
    setBillFile(null);
    setBillSubmitting(false);
    billForm.resetFields();
    setBillModalOpen(true);
  };

  const onSubmitBill = () => {
    if (billSubmitting) return;
    billForm.validateFields().then((values) => {
      if (!billFile) {
        message.error("Please select a bill file to upload");
        return;
      }
      const formData = new FormData();
      formData.append("file", billFile);
      formData.append("actualAmount", values.actualAmount);

      setBillSubmitting(true);
      request({
        url: apiPath.reimbursementBill(activeRecord._id),
        method: "POST",
        data: formData,
        header: { "Content-Type": "multipart/form-data" },
        onSuccess: () => {
          setBillSubmitting(false);
          message.success("Bill submitted");
          setBillModalOpen(false);
          fetchMine();
        },
        onError: (err) => {
          setBillSubmitting(false);
          message.error(err?.response?.data?.message || "Could not submit bill");
        },
      });
    });
  };

  const onDownloadBill = async (record) => {
    try {
      const token = localStorage.getItem("i_ems_employee_token") || "";
      const response = await axios.get(`${apiPath.baseURL}${apiPath.reimbursementBillDownload(record._id)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", record.billFileName || "bill");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Could not download bill");
    }
  };

  const columns = [
    { title: "Category", dataIndex: "category" },
    { title: "Description", dataIndex: "description", ellipsis: true },
    {
      title: "Estimated Amount",
      dataIndex: "estimatedAmount",
      render: (v, r) => `${currencySymbol(r.currency || "PHP")}${(v || 0).toLocaleString()}`,
    },
    {
      title: "Actual Amount",
      dataIndex: "actualAmount",
      render: (v, r) => (v != null ? `${currencySymbol(r.currency || "PHP")}${v.toLocaleString()}` : "-"),
    },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
    { title: "Comment", dataIndex: "adminComment" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.status === "approved" && (
            <Button size="small" icon={<UploadOutlined />} onClick={() => openBillModal(record)}>
              Upload Bill
            </Button>
          )}
          {record.billStoredFileName && (
            <Button size="small" icon={<DownloadOutlined />} onClick={() => onDownloadBill(record)}>
              Bill
            </Button>
          )}
          {record.status === "pending" && (
            <Popconfirm title="Cancel this reimbursement request?" onConfirm={() => onCancelRequest(record)}>
              <Button size="small" danger>
                Cancel
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader title="Reimbursements" subtitle="Request and track your expense reimbursements" />

      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "flex-end" }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Request Reimbursement
        </Button>
      </Space>
      <Card>
        <div style={{ overflowX: "auto" }}>
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={requests}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <EmptyState message="No reimbursement requests yet" actionLabel="Request Reimbursement" onAction={openCreate} /> }}
          />
        </div>
      </Card>

      <Modal
        title="Request Reimbursement"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={submitting}
        okButtonProps={{ disabled: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select options={CATEGORY_OPTIONS} placeholder="Select category" />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Form.Item name="currency" label="Currency" initialValue="PHP" style={{ width: 130, flexShrink: 0 }}>
              <Select options={CURRENCY_OPTIONS} />
            </Form.Item>
            <Form.Item name="estimatedAmount" label="Estimated Amount" rules={[{ required: true }]} style={{ flex: 1 }}>
              {/* Keyed to the currency so antd's InputNumber remounts and
                  re-runs its formatter — it only does that on its own value
                  changing, not just because this prop's closure changed. */}
              <InputNumber key={requestCurrency} style={{ width: "100%" }} min={0} prefix={currencySymbol(requestCurrency)} />
            </Form.Item>
          </div>
          <Form.Item label="Receipt/Bill (PDF/JPEG/PNG, optional)">
            <Upload
              beforeUpload={(file) => {
                setReceiptFile(file);
                return false;
              }}
              onRemove={() => setReceiptFile(null)}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png"
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Upload Bill"
        open={billModalOpen}
        onCancel={() => setBillModalOpen(false)}
        onOk={onSubmitBill}
        confirmLoading={billSubmitting}
        okButtonProps={{ disabled: billSubmitting }}
        cancelButtonProps={{ disabled: billSubmitting }}
        destroyOnClose
      >
        <Form layout="vertical" form={billForm}>
          <Form.Item label="Bill File (PDF/JPEG/PNG)" required>
            <Upload
              beforeUpload={(file) => {
                setBillFile(file);
                return false;
              }}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png"
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="actualAmount" label={`Actual Amount (${activeRecord?.currency || "PHP"})`} rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} prefix={currencySymbol(activeRecord?.currency || "PHP")} />
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default Reimbursement;
